/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable no-unreachable */
import { SEO_API_BASE_URL } from '@/core/constants/appConfig/environment';
import { callAPI } from '@/shared/fetch_client';
import { Logger } from '@/shared/logger/logger';
import { getEventsWorker } from '@/workers/init_workers';
import { SourcesMableEvent } from '@mable-ai/seo-client';

export type PostMableEventRequest = {
  mableEvent: SourcesMableEvent;
};

// API Service
const MableEventApi = {
  route: '/seo/events',

  /**
   * Sends a Mable event to the SEO API.
   * Uses a background worker if available, otherwise falls back to direct API call.
   * This implementation is non-blocking and won't interrupt the main thread.
   * @param data - The data containing the Mable event.
   * @returns A Promise that resolves to a boolean indicating success or failure.
   */
  sendMableEvent(data: PostMableEventRequest): Promise<boolean> {
    // Create a non-blocking promise that will be resolved later
    return new Promise<boolean>(resolve => {
      // Use setTimeout with 0ms delay to move execution to the end of the event loop
      // This prevents blocking the main thread
      setTimeout(async () => {
        try {
          // This does the type validation! ( SourcesMableEvent is expected type exported from seo)
          const mableEvent = data.mableEvent satisfies SourcesMableEvent;

          // Get worker manager if available
          const workerManager = getEventsWorker();

          // Use worker if available
          if (workerManager?.isInitialized()) {
            Logger.debug('Using worker to send Mable event');

            // Create a trace ID based on event name or ID if available
            const traceId = `mable-${mableEvent.en || 'event'}-${mableEvent.eid || Date.now()}`;

            // Use Logger.profileFnAsync instead of manual profiling
            void Logger.profileFnAsync(
              'submitTask:SEND_MABLE_EVENT',
              () =>
                workerManager.submitTask(
                  'SEND_MABLE_EVENT',
                  {
                    apiUrl: `${SEO_API_BASE_URL}${MableEventApi.route}`,
                    mableEvent: JSON.parse(JSON.stringify(mableEvent)),
                  },
                  (error, _result) => {
                    if (error) {
                      Logger.error(
                        'Error in background SEND_MABLE_EVENT task',
                        error,
                      );
                    } else {
                      Logger.debug(
                        'Background SEND_MABLE_EVENT task completed',
                      );
                    }
                  },
                ),
              traceId,
            );

            // Resolve immediately since we're not waiting for the worker
            resolve(true);
          }
          // Fall back to direct API call
          else {
            Logger.debug(
              'Worker not available, using direct API call for Mable event',
            );

            // Create a trace ID based on event name or ID if available
            const traceId = `mable-direct-${mableEvent.en || 'event'}-${mableEvent.eid || Date.now()}`;

            const response = await Logger.profileFnAsync(
              'directAPI:SEND_MABLE_EVENT',
              async () =>
                callAPI<boolean>({
                  body: JSON.stringify(mableEvent),
                  method: 'POST',
                  url: `${SEO_API_BASE_URL}${MableEventApi.route}`,
                }),
              traceId,
            );

            resolve(response.data);
          }
        } catch (error) {
          Logger.error('Error sending Mable event', error);
          resolve(false);
        }
      }, 0);
    });
  },
};

export default MableEventApi;
